<script lang="ts">
  /**
   * How a run or a match ends, on the deep ground (`DeepScreen`): what it
   * was, how it went, the facts that put it in context, and two ways on.
   * TwinTen's solo, co-op and versus results; Interlock's sprint and duel.
   *
   * Top to bottom, each part optional but one heading:
   *
   * - `celebrate` with `celebrateLabels`: a ribbon over everything, distinct
   *   for a new personal best (`pb`) and a new record (`record`). The game's
   *   `art` (confetti, a pattern) is handed the same value, behind it all.
   * - `eyebrow` (small capitals: the level, "Game ended"); `tag` (a pill:
   *   "Expert"); `headline` (the screen's `<h1>`; without one the eyebrow is
   *   the heading). `focusHeading` moves focus to it when the screen opens.
   * - `figure` (the score, the time) at the display step, with an optional
   *   `figureIcon` in gold before it, and `figureCaption` under it.
   * - `notice`: the game's lines right under the figure (coins earned, a
   *   tournament rank, a guest's "Sign in to post it").
   * - `standings` (1.7.0): a ranking of two to N players (`Standings`, in its
   *   deep tone): a race on one device, a round of runs in turn.
   * - `extra`: the game's own block (the final board in miniature, two
   *   players side by side).
   * - `facts`: label and value pairs, as `rows` (a panel with a title, and
   *   `factsAside` on its right) or `tiles` (three across). A fact with
   *   `emphasis` is marked as what the celebration is about; `detail` is a
   *   quiet line under its label (a record's holder and the gap to it);
   *   `delta` a small success pill beside the value.
   * - `after`: the game's block under the facts (a head-to-head, fun tallies).
   * - `actionsNote`: a line over the buttons (an invitation that arrived).
   * - `primary` (a light pill), `secondary` (an outline pill, or a bare word
   *   with `secondaryStyle="text"`).
   * - `actions` (since 1.7.0): more ways on, under the secondary, each a
   *   bare word by default (`actionsStyle="outline"` for the secondary's
   *   outline): "See the board", "Share". The primary stays the one that
   *   stands out; these never compete with it.
   *
   * Test ids (`testids`, each with a default): `root` `ui-results`,
   * `celebrate` `ui-results-celebrate`, `eyebrow` `ui-results-eyebrow`, `tag`
   * `ui-results-tag`, `headline` `ui-results-headline`, `figure`
   * `ui-results-figure`, `facts` `ui-results-facts`, `factsAside`
   * `ui-results-facts-aside`; a fact or a button takes its own `testid`.
   */
  import type { Snippet } from 'svelte';

  import DeepScreen from '../components/DeepScreen.svelte';
  import Icon from '../components/Icon.svelte';
  import type { IconName } from '../icons.js';
  import Standings from '../components/Standings.svelte';
  import type { ResultCelebration, ResultFact, StandingsBlock } from './playTypes.js';
  import type { TemplateAction } from '../types.js';

  interface Props {
    readonly eyebrow?: string | undefined;
    readonly tag?: string | undefined;
    readonly headline?: string | undefined;
    readonly focusHeading?: boolean;
    readonly figure?: string | undefined;
    readonly figureIcon?: IconName | undefined;
    readonly figureCaption?: string | undefined;
    readonly celebrate?: ResultCelebration | undefined;
    readonly celebrateLabels?: { readonly pb?: string; readonly record?: string } | undefined;
    readonly art?: Snippet<[ResultCelebration]> | undefined;
    readonly notice?: Snippet | undefined;
    /** A ranking of the players (1.7.0), drawn under `notice`. */
    readonly standings?: StandingsBlock | undefined;
    readonly extra?: Snippet | undefined;
    readonly facts?: readonly ResultFact[] | undefined;
    readonly factsLayout?: 'rows' | 'tiles';
    readonly factsTitle?: string | undefined;
    readonly factsAside?: string | undefined;
    readonly after?: Snippet | undefined;
    readonly actionsNote?: Snippet | undefined;
    readonly primary?: TemplateAction | undefined;
    readonly secondary?: TemplateAction | undefined;
    readonly secondaryStyle?: 'outline' | 'text';
    // ── 1.7.0 ──
    /** More ways on, under the secondary, in order ("See the board"). */
    readonly actions?: readonly TemplateAction[] | undefined;
    /** How `actions` are drawn: a bare word (default) or the secondary's outline. */
    readonly actionsStyle?: 'outline' | 'text';
    readonly testids?:
      | {
          readonly root?: string;
          readonly celebrate?: string;
          readonly eyebrow?: string;
          readonly tag?: string;
          readonly headline?: string;
          readonly figure?: string;
          readonly facts?: string;
          readonly factsAside?: string;
        }
      | undefined;
  }
  const {
    eyebrow,
    tag,
    headline,
    focusHeading = false,
    figure,
    figureIcon,
    figureCaption,
    celebrate = null,
    celebrateLabels,
    art,
    notice,
    standings,
    extra,
    facts = [],
    factsLayout = 'rows',
    factsTitle,
    factsAside,
    after,
    actionsNote,
    primary,
    secondary,
    secondaryStyle = 'outline',
    actions = [],
    actionsStyle = 'text',
    testids,
  }: Props = $props();

  const ribbon = $derived(
    celebrate === null || celebrateLabels === undefined ? undefined : celebrateLabels[celebrate],
  );

  let heading = $state<HTMLElement | null>(null);
  $effect(() => {
    if (focusHeading && heading !== null) heading.focus({ preventScroll: true });
  });

  const EMPHASIS_ICON: Record<'pb' | 'record', IconName> = { pb: 'starFilled', record: 'trophy' };
</script>

{#snippet backdrop()}
  {#if art !== undefined}{@render art(celebrate)}{/if}
{/snippet}

<DeepScreen art={art === undefined ? undefined : backdrop} testid={testids?.root ?? 'ui-results'}>
  <div class="ui-results" data-celebrate={celebrate ?? undefined}>
    {#if ribbon !== undefined && celebrate !== null}
      <p
        class="ui-results-ribbon ui-results-ribbon--{celebrate}"
        role="status"
        data-testid={testids?.celebrate ?? 'ui-results-celebrate'}
      >
        <Icon name={EMPHASIS_ICON[celebrate]} size={16} color="var(--ui-gold)" />
        {ribbon}
      </p>
    {/if}

    <div class="ui-results-head">
      {#if eyebrow !== undefined}
        {#if headline === undefined}
          <h1
            class="ui-results-eyebrow"
            tabindex="-1"
            bind:this={heading}
            data-testid={testids?.eyebrow ?? 'ui-results-eyebrow'}
          >
            {eyebrow}
          </h1>
        {:else}
          <p class="ui-results-eyebrow" data-testid={testids?.eyebrow ?? 'ui-results-eyebrow'}>
            {eyebrow}
          </p>
        {/if}
      {/if}
      {#if tag !== undefined}
        <p class="ui-results-tag" data-testid={testids?.tag ?? 'ui-results-tag'}>{tag}</p>
      {/if}
      {#if headline !== undefined}
        <h1
          class="ui-results-headline"
          tabindex="-1"
          bind:this={heading}
          data-testid={testids?.headline ?? 'ui-results-headline'}
        >
          {headline}
        </h1>
      {/if}
      {#if figure !== undefined}
        <p class="ui-results-figure" data-testid={testids?.figure ?? 'ui-results-figure'}>
          {#if figureIcon !== undefined}
            <Icon name={figureIcon} size={26} color="var(--ui-gold)" />
          {/if}
          {figure}
        </p>
      {/if}
      {#if figureCaption !== undefined}
        <p class="ui-results-caption">{figureCaption}</p>
      {/if}
    </div>

    {#if notice !== undefined}
      <div class="ui-results-notice">{@render notice()}</div>
    {/if}

    {#if standings !== undefined}
      <Standings
        rows={standings.rows}
        label={standings.label}
        winnerLabel={standings.winnerLabel}
        formatRank={standings.formatRank}
        size={standings.size ?? 'full'}
        tone="deep"
        testid={standings.testid ?? 'ui-results-standings'}
      />
    {/if}

    {#if extra !== undefined}{@render extra()}{/if}

    {#if facts.length > 0}
      <section
        class="ui-results-facts ui-results-facts--{factsLayout}"
        data-testid={testids?.facts ?? 'ui-results-facts'}
      >
        {#if factsTitle !== undefined && factsLayout === 'rows'}
          <div class="ui-results-facts-title">
            <span>{factsTitle}</span>
            {#if factsAside !== undefined}
              <span
                class="ui-results-facts-aside"
                data-testid={testids?.factsAside ?? 'ui-results-facts-aside'}>{factsAside}</span
              >
            {/if}
          </div>
        {/if}
        <dl class="ui-results-list">
          {#each facts as fact (fact.key)}
            <div
              class="ui-results-fact"
              class:ui-results-fact--marked={fact.emphasis === 'pb' || fact.emphasis === 'record'}
              data-emphasis={fact.emphasis ?? undefined}
              data-testid={fact.testid}
            >
              <dt class="ui-results-fact-label">
                <span>{fact.label}</span>
                {#if fact.detail !== undefined}
                  <span class="ui-results-fact-detail">{fact.detail}</span>
                {/if}
              </dt>
              <dd class="ui-results-fact-value">
                {#if fact.delta !== undefined}
                  <span class="ui-results-fact-delta">{fact.delta}</span>
                {/if}
                {#if fact.emphasis === 'pb' || fact.emphasis === 'record'}
                  <Icon name={EMPHASIS_ICON[fact.emphasis]} size={14} color="var(--ui-gold)" />
                {/if}
                <b>{fact.value}</b>
              </dd>
            </div>
          {/each}
        </dl>
      </section>
    {/if}

    {#if after !== undefined}{@render after()}{/if}

    {#if actionsNote !== undefined || primary !== undefined || secondary !== undefined || actions.length > 0}
      <div class="ui-results-actions">
        {#if actionsNote !== undefined}{@render actionsNote()}{/if}
        {#if primary !== undefined}
          <button
            type="button"
            class="ui-results-primary"
            class:ui-results-action--icon={primary.icon !== undefined}
            disabled={primary.disabled ?? false}
            aria-busy={primary.busy === true ? 'true' : undefined}
            data-testid={primary.testid}
            onclick={() => {
              if (primary.busy !== true) primary.onclick();
            }}
          >
            {#if primary.icon !== undefined}<span class="ui-results-icon" aria-hidden="true"
                >{@render primary.icon()}</span
              >{/if}{primary.label}
          </button>
        {/if}
        {#if secondary !== undefined}
          <button
            type="button"
            class="ui-results-secondary ui-results-secondary--{secondaryStyle}"
            class:ui-results-action--icon={secondary.icon !== undefined}
            disabled={secondary.disabled ?? false}
            data-testid={secondary.testid}
            onclick={secondary.onclick}
          >
            {#if secondary.icon !== undefined}<span class="ui-results-icon" aria-hidden="true"
                >{@render secondary.icon()}</span
              >{/if}{secondary.label}
          </button>
        {/if}
        {#each actions as action, index (index)}
          <button
            type="button"
            class="ui-results-secondary ui-results-secondary--{actionsStyle}"
            class:ui-results-action--icon={action.icon !== undefined}
            disabled={action.disabled ?? false}
            aria-busy={action.busy === true ? 'true' : undefined}
            data-testid={action.testid}
            onclick={() => {
              if (action.busy !== true) action.onclick();
            }}
          >
            {#if action.icon !== undefined}<span class="ui-results-icon" aria-hidden="true"
                >{@render action.icon()}</span
              >{/if}{action.label}
          </button>
        {/each}
      </div>
    {/if}
  </div>
</DeepScreen>

<style>
  .ui-results {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .ui-results-ribbon {
    align-self: center;
    margin: 0;
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.3rem 0.85rem;
    border-radius: var(--ui-radius-pill);
    font: var(--ui-text-label);
    font-weight: 800;
  }

  /* A personal best: the light pill on the deep ground. */
  .ui-results-ribbon--pb {
    background: var(--ui-on-deep);
    color: var(--ui-deep-ink);
  }

  /* A record: louder, a gold ring around the on-deep words. */
  .ui-results-ribbon--record {
    border: 2px solid var(--ui-gold);
    color: var(--ui-on-deep);
  }

  .ui-results-head {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }

  .ui-results-eyebrow {
    margin: 0;
    font: var(--ui-text-eyebrow);
    letter-spacing: var(--ui-tracking-eyebrow);
    text-transform: uppercase;
    outline: none;
  }

  /* On the gradient's dark stop, a gated pair (on-deep on deep-to): a wash of
     on-deep over the light end fell under 4.5:1 in the house theme. */
  .ui-results-tag {
    margin: 0;
    padding: 0.15rem 0.6rem;
    border-radius: var(--ui-radius-pill);
    background: var(--ui-deep-to);
    font: var(--ui-text-eyebrow);
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .ui-results-headline {
    margin: 0;
    font: var(--ui-text-display);
    line-height: 1.1;
    outline: none;
  }

  .ui-results-figure {
    margin: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    font: var(--ui-text-display);
    font-variant-numeric: tabular-nums;
    line-height: 1.1;
  }

  .ui-results-caption {
    margin: 0;
    font: var(--ui-text-label);
    font-weight: 400;
    opacity: 0.9;
  }

  .ui-results-notice {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    font: var(--ui-text-label);
  }

  .ui-results-facts {
    border-radius: var(--ui-radius-md);
    text-align: left;
  }

  .ui-results-facts--rows {
    padding: 0.5rem 1rem;
    background: color-mix(in srgb, var(--ui-on-deep) 16%, transparent);
  }

  .ui-results-facts-title {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.5rem 0.25rem;
    font: var(--ui-text-body);
    font-weight: 700;
    opacity: 0.85;
  }

  .ui-results-facts-aside {
    font: var(--ui-text-eyebrow);
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .ui-results-list {
    margin: 0;
  }

  .ui-results-facts--rows .ui-results-fact {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.6rem 0.25rem;
    border-top: 1px solid color-mix(in srgb, var(--ui-on-deep) 20%, transparent);
  }

  .ui-results-facts--rows .ui-results-fact:first-child {
    border-top: 0;
  }

  .ui-results-facts--rows .ui-results-facts-title + .ui-results-list .ui-results-fact:first-child {
    border-top: 1px solid color-mix(in srgb, var(--ui-on-deep) 20%, transparent);
  }

  .ui-results-fact--marked {
    border-radius: var(--ui-radius-sm);
    background: color-mix(in srgb, var(--ui-on-deep) 14%, transparent);
  }

  .ui-results-fact-label {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    min-width: 0;
    font: var(--ui-text-body);
  }

  .ui-results-fact-detail {
    font: var(--ui-text-caption);
    opacity: 0.9;
  }

  .ui-results-fact-value {
    margin: 0;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font: var(--ui-text-body);
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  .ui-results-fact-value b {
    font-weight: 700;
  }

  .ui-results-fact-delta {
    padding: 0.15rem 0.45rem;
    border-radius: var(--ui-radius-pill);
    background: var(--ui-good);
    color: var(--ui-on-accent);
    font: var(--ui-text-caption);
    font-weight: 700;
  }

  /* Three across: a fact each, the label in small capitals over the value. */
  .ui-results-facts--tiles .ui-results-list {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.5rem;
  }

  .ui-results-facts--tiles .ui-results-fact {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.2rem;
    padding: 0.7rem 0.4rem;
    border-radius: var(--ui-radius-md);
    background: color-mix(in srgb, var(--ui-on-deep) 12%, transparent);
    text-align: center;
  }

  .ui-results-facts--tiles .ui-results-fact-label {
    align-items: center;
    font: var(--ui-text-eyebrow);
    letter-spacing: var(--ui-tracking-eyebrow);
    text-transform: uppercase;
    opacity: 0.8;
  }

  .ui-results-facts--tiles .ui-results-fact-value {
    font: var(--ui-text-heading);
  }

  .ui-results-actions {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0.6rem;
    margin-top: 0.5rem;
  }

  .ui-results-primary {
    min-height: 3rem;
    padding: 0.9rem 1.5rem;
    border: 0;
    border-radius: var(--ui-radius-pill);
    /* The light pill: on-deep stays light in every theme, where a surface
       would be near-black on a dark one and vanish into the ground. */
    background: var(--ui-on-deep);
    color: var(--ui-deep-ink);
    font: var(--ui-text-body);
    font-weight: 800;
  }

  .ui-results-primary:disabled {
    opacity: 0.6;
  }

  .ui-results-secondary {
    min-height: 2.75rem;
    padding: 0.6rem 1.5rem;
    border-radius: var(--ui-radius-pill);
    background: transparent;
    color: var(--ui-on-deep);
    font: var(--ui-text-body);
    font-weight: 700;
  }

  .ui-results-secondary--outline {
    border: 1px solid color-mix(in srgb, var(--ui-on-deep) 65%, transparent);
  }

  .ui-results-secondary--text {
    border: 0;
  }

  /* A glyph before the words (since 1.3.0): side by side, centred. */
  .ui-results-action--icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--ui-space-2);
  }

  .ui-results-icon {
    display: inline-flex;
    width: 1.1rem;
    height: 1.1rem;
  }

  .ui-results-icon :global(svg) {
    width: 100%;
    height: 100%;
  }

  .ui-results-primary:focus-visible,
  .ui-results-secondary:focus-visible {
    outline: 3px solid var(--ui-on-deep);
    outline-offset: 2px;
  }
</style>
